import { DeleteOutlineRounded, EditOutlined, IosShare } from "@mui/icons-material";
import { Box, Button, Chip, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { downloadEncodedUri, setTitlePrefix, useDestructiveConfirm } from "src/utilities";
import { Comments, EditMetricModal, HistoryLedgerMapper, Chart, LinkBehavior, ShareButton } from "../../components";
import { CommentSubject, ConnectObjectType, Links } from "../../constants";
import { useBreakpoints } from "../../hooks";
import { useDeleteMetric, useGetMetric } from "../../http";
import { MetricMapper } from "../../mapping";
import { DetailPageLayout } from "./DetailPageLayout";
import { MetricHistoryLedgerMappingStrategy } from "./components";

interface UrlParams {
  metricGuid: string;
}

const MetricDetailsPage = () => {
  const { metricGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { data: metric, isLoading } = useGetMetric(metricGuid);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { mutate: deleteMetric } = useDeleteMetric();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const confirm = useDestructiveConfirm();
  const { isSmUp } = useBreakpoints();

  useEffect(() => {
    setTitlePrefix(metric?.title);
  }, [metric?.title]);

  function handleExportClicked() {
    if (metric === undefined) {
      return;
    }

    const csvData = MetricMapper.toCsvData(metric);

    downloadEncodedUri({
      filename: `${DateTime.now().toISODate()} - ${metric.title}`,
      encodedUri: encodeURI(`data:text/csv;chartset=utf-8,${csvData}`),
    });
  }

  async function handleDeleteClicked() {
    if (metric === undefined) {
      return;
    }

    const { confirmed } = await confirm({
      title: `Delete ${ConnectObjectType.Metric}`,
      content: `Are you sure you want to delete this ${ConnectObjectType.Metric}?`,
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMetric(
      {
        assignedTeamSlug: metric.assignedTeam.slug,
        guid: metric.guid,
      },
      {
        onSuccess: () => {
          if (metric !== undefined) {
            navigate(Links.TeamMetrics(metric.assignedTeam.slug));
            enqueueSnackbar(`Metric has been deleted`);
          }
        },
      }
    );
  }

  return (
    <DetailPageLayout
      actions={
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
          <ShareButton variant="outlined" href={window.location.href} />
          <Button variant="outlined" startIcon={<IosShare />} onClick={handleExportClicked}>
            Export CSV
          </Button>
          {metric?.userCanEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => {
                  setIsModalVisible(true);
                }}
              >
                Edit
              </Button>
              <Divider orientation="vertical" />
              <Button variant="outlined" color="error" onClick={handleDeleteClicked} sx={{ minWidth: 18 }}>
                <DeleteOutlineRounded />
              </Button>
            </>
          )}
        </Stack>
      }
    >
      {isLoading && (
        <Stack>
          <Skeleton sx={{ mx: { xs: 0.5, sm: 1 }, my: 0.5 }} width={"10%"} />
          <Divider />
          <Skeleton sx={{ mx: { xs: 0.5, sm: 1 } }} height={96} />
        </Stack>
      )}

      {!isLoading && metric && (
        <>
          <Stack spacing={{ xs: 0.5, sm: 1 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 0.5, sm: 1 } }}
            >
              <Stack direction="row" gap={0.5} flexWrap="wrap">
                {metric.assignedTeam && (
                  <Chip
                    label={metric.assignedTeam.name}
                    color="primary"
                    component={LinkBehavior}
                    href={Links.Team(metric.assignedTeam.slug)}
                    clickable
                  />
                )}

                {metric.reportingUser && (
                  <Chip
                    label={metric.reportingUser.displayName}
                    color="wine"
                    component={LinkBehavior}
                    href={Links.User(metric.reportingUser.userId)}
                    clickable
                  />
                )}
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={0.5} sx={{ px: { xs: 0.5, sm: 1 }, minHeight: 300, justifyContent: "space-between" }}>
              <Typography variant={isSmUp ? "h3" : "h4"}>{metric.title}</Typography>

              {metric.description && <Typography>{metric.description}</Typography>}
              {metric.datasets.length > 0 && (
                <>
                  <Chart
                    datasets={MetricMapper.toChartDataWithNulls(metric).map((data) => {
                      return {
                        title: data.title,
                        displayAsBar: data.displayAsBar,
                        color: data.color,
                        dotted: data.dotted,
                        data: data.chartData,
                        sortOrder: data.sortOrder,
                      };
                    })}
                    annotations={metric.annotations}
                    showXAxis
                    showYAxis
                    sx={{ height: { xs: "60vw", sm: "250px" } }}
                  />
                  {metric.dataLastModifiedDateTimeUtc && metric.dataLastModifiedByDisplayName && (
                    <Box>
                      <Typography variant="caption">{`Updated by ${
                        metric.dataLastModifiedByDisplayName
                      } on ${DateTime.fromISO(metric.dataLastModifiedDateTimeUtc, { zone: "utc" })
                        .toLocal()
                        .toLocaleString(DateTime.DATE_FULL)}`}</Typography>
                    </Box>
                  )}
                </>
              )}
            </Stack>
            <Box>
              <Divider />
              <Comments
                subjectType={CommentSubject.PlanMetric}
                subjectGuid={metricGuid}
                additionalComments={HistoryLedgerMapper.toComments(
                  metric.historyLedger.map((x) => ({
                    changes: x.changes,
                    changeType: x.changeType,
                    timestampUtc: DateTime.fromISO(x.timestampUtc, { zone: "utc" }),
                    user: x.user,
                  })),
                  new MetricHistoryLedgerMappingStrategy()
                )}
              />
            </Box>
          </Stack>
          <EditMetricModal
            isVisible={isModalVisible}
            metric={metric}
            onUpdated={() => setIsModalVisible(false)}
            onCancelled={() => setIsModalVisible(false)}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export { MetricDetailsPage };
